import { createTheme, CSSVariablesResolver, MantineColorsTuple } from '@mantine/core'

const grapefruit: MantineColorsTuple = [
  '#fff0ef', '#ffe0de', '#fdb8b6', '#fc8e8b', '#fd6a66',
  '#fd544f', '#fd4843', '#e23835', '#ca2e2e', '#b01f24',
]

const eveningSea: MantineColorsTuple = [
  '#eefaf7', '#d7f0e8', '#b0e2d1', '#86d4b9', '#63c6a4',
  '#4DAA8A', '#3f9175', '#337360', '#285c4d', '#1b4439',
]

// Override Mantine's default grey-dark palette with brand teal-dark
const dark: MantineColorsTuple = [
  '#C8D8DC', '#9DB8C2', '#7099A8', '#4A7A8E', '#2D5C70',
  '#1E3D4D', '#162632', '#0D1B22', '#091418', '#050E12',
]

export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--sat-bg':           '#F8EDE0',
    '--sat-surface':      '#ffffff',
    '--sat-surface-dim':  '#F8EDE0',
    '--sat-surface-alt':  '#faf7f3',
    '--sat-surface-success': '#E8F5EF',
    '--sat-surface-warning': '#FFF8DC',
    '--sat-surface-info': '#EDEEFF',
    '--sat-surface-danger': '#FFF0F0',
    '--sat-border':       '#e4cfc0',
    '--sat-border-soft':  '#eeeeee',
    '--sat-border-faint': '#f5f5f5',
    '--sat-border-warm':  '#e0d0c0',
    '--sat-preview-border': '#e8ddd2',
    '--sat-text':         '#10252A',
    '--sat-text-dim':     '#4a6068',
    '--sat-text-muted':   '#8a9fa8',
    '--sat-text-soft':    '#666666',
    '--sat-text-subtle':  '#555555',
    '--sat-text-faint':   '#999999',
    '--sat-icon-muted':   '#cccccc',
    '--sat-green':        '#4DAA8A',
    '--sat-red':          '#FD5956',
    '--sat-yellow':       '#FECF24',
    '--sat-blush':        '#E2B5A6',
    '--sat-warning-text': '#B8860B',
    '--sat-info-text':    '#5C6BC0',
    '--sat-red-soft':     'rgba(253,89,86,0.08)',
    '--sat-red-soft-strong': 'rgba(253,89,86,0.12)',
    '--sat-red-soft-alt': 'rgba(253,89,86,0.15)',
    '--sat-green-soft':   'rgba(77,170,138,0.08)',
    '--sat-green-soft-subtle': 'rgba(77,170,138,0.05)',
    '--sat-green-soft-strong': 'rgba(77,170,138,0.12)',
    '--sat-green-border-soft': 'rgba(77,170,138,0.20)',
    '--sat-green-border-medium': 'rgba(77,170,138,0.40)',
    '--sat-yellow-soft':  'rgba(254,207,36,0.28)',
    '--sat-yellow-glow':  'rgba(254,207,36,0.35)',
    '--sat-neutral-soft': 'rgba(148,163,184,0.14)',
    '--sat-nav-bg':       '#ffffff',
    '--sat-nav-border':   '#e9ecef',
    '--sat-shadow':       'rgba(16,37,42,0.10)',
    '--sat-shadow-soft':  'rgba(0,0,0,0.04)',
    '--sat-shadow-strong': 'rgba(0,0,0,0.10)',
    '--sat-hero-gradient': 'linear-gradient(135deg, rgba(248,237,224,0.98) 0%, rgba(255,255,255,0.98) 50%, rgba(226,181,166,0.28) 100%)',
    '--sat-hero-bubble':  'rgba(253,89,86,0.10)',
    '--sat-radar-grid':   'rgba(77,170,138,0.18)',
    '--sat-radar-fill':   'rgba(77,170,138,0.12)',
    '--sat-radar-line':   'rgba(77,170,138,0.14)',
  },
  dark: {
    '--sat-bg':           '#0D1B22',
    '--sat-surface':      '#162632',
    '--sat-surface-dim':  '#111f2a',
    '--sat-surface-alt':  '#13222c',
    '--sat-surface-success': 'rgba(79,209,160,0.12)',
    '--sat-surface-warning': 'rgba(254,207,36,0.16)',
    '--sat-surface-info': 'rgba(92,107,192,0.18)',
    '--sat-surface-danger': 'rgba(255,107,104,0.12)',
    '--sat-border':       '#1e3040',
    '--sat-border-soft':  '#2a3b4a',
    '--sat-border-faint': '#223340',
    '--sat-border-warm':  '#2a3b4a',
    '--sat-preview-border': '#2a3b4a',
    '--sat-text':         '#EDE0D4',
    '--sat-text-dim':     '#9db8c2',
    '--sat-text-muted':   '#5c7a87',
    '--sat-text-soft':    '#b8c7cd',
    '--sat-text-subtle':  '#9ab0b8',
    '--sat-text-faint':   '#7c919a',
    '--sat-icon-muted':   '#6f8791',
    '--sat-green':        '#4fd1a0',
    '--sat-red':          '#FF6B68',
    '--sat-yellow':       '#FECF24',
    '--sat-blush':        '#c49080',
    '--sat-warning-text': '#FECF24',
    '--sat-info-text':    '#9AA9FF',
    '--sat-red-soft':     'rgba(255,107,104,0.08)',
    '--sat-red-soft-strong': 'rgba(255,107,104,0.12)',
    '--sat-red-soft-alt': 'rgba(255,107,104,0.18)',
    '--sat-green-soft':   'rgba(79,209,160,0.08)',
    '--sat-green-soft-subtle': 'rgba(79,209,160,0.05)',
    '--sat-green-soft-strong': 'rgba(79,209,160,0.12)',
    '--sat-green-border-soft': 'rgba(79,209,160,0.20)',
    '--sat-green-border-medium': 'rgba(79,209,160,0.40)',
    '--sat-yellow-soft':  'rgba(254,207,36,0.22)',
    '--sat-yellow-glow':  'rgba(254,207,36,0.28)',
    '--sat-neutral-soft': 'rgba(255,255,255,0.07)',
    '--sat-nav-bg':       '#0D1B22',
    '--sat-nav-border':   '#1e3040',
    '--sat-shadow':       'rgba(0,0,0,0.35)',
    '--sat-shadow-soft':  'rgba(0,0,0,0.18)',
    '--sat-shadow-strong': 'rgba(0,0,0,0.30)',
    '--sat-hero-gradient': 'linear-gradient(135deg, rgba(13,27,34,0.98) 0%, rgba(22,38,50,0.98) 50%, rgba(79,209,160,0.12) 100%)',
    '--sat-hero-bubble':  'rgba(255,107,104,0.08)',
    '--sat-radar-grid':   'rgba(79,209,160,0.22)',
    '--sat-radar-fill':   'rgba(79,209,160,0.14)',
    '--sat-radar-line':   'rgba(79,209,160,0.18)',
  },
})

export const theme = createTheme({
  primaryColor: 'grapefruit',
  colors: { grapefruit, eveningSea, dark },
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  headings: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  other: {
    ambientGlow:     '#F8EDE0',
    terraCottaBlush: '#E2B5A6',
    grapefruit:      '#FD5956',
    lemonPunch:      '#FECF24',
    eveningSea:      '#4DAA8A',
    sunkenShip:      '#10252A',
  },
})
